import React from 'react';
import { abbreviateNumber } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import { Formatter, LabelFormatter, LegendType } from '../../../../charts/core/Chart.types';
import { PieSeries } from '../../../../charts/radial/pie/PieSeries';
import { formatNumberCompact } from '../../../../components/Widgets/CustomChartLabel';
import { useAvContext } from '../../../../context/AvContextProvider';
import { NO_VALUE } from '../../constants';
import { DonutTypeWidget, PieBasicLegendPosition, PieLegendStyles, PieSubType } from '../../types';
import { PieTooltipComponent } from './PieSeriesTooltipComponent';
import type { PieWidgetProps } from './PieWidget';

export type PieWidgetDataEntry = { [x: string]: any; headCellType: string | undefined; dim?: string; metric: string; originalValue: any };

type PieSeriesAdapterProps = {
  data: PieWidgetDataEntry[];
  metric: string;
  dimNameOrAlias: string;
  getColorFromSeries: (args: { dimValue: string | undefined; metricValue: number; index: number }) => string;
  legendProps?: {
    metricNameFormatter?: (v: any) => any;
    showValue?: boolean;
    showShare?: boolean;
    variant?: string;
    maxMetricWidth?: number | string;
  };
  selected?: { value: any; color: string; index: number; headCellType?: string; originalValue?: any }[];
  metricDisplayName?: string | number | null | undefined;
  measurements: ReturnType<typeof useAvContext>['measurements'];
  customLabelRenderer?: (value: any) => React.ReactNode;
} & Pick<PieWidgetProps, 'responseTotals' | 'clickInteractions' | 'onSelect' | 'widget'>;

export type DatumWithColor = {
  color: string;
  headCellType: string | undefined;
  dim?: string;
  metric: string;
  originalValue: any;
};

export type InteractionArgs = {
  d: PieWidgetDataEntry;
  widgetData: PieWidgetDataEntry[];
  index: number;
  originalValue: DatumWithColor['originalValue'];
  headCellType: DatumWithColor['headCellType'];
};

export const PieSeriesAdapter = ({
  data,
  widget,
  metric,
  dimNameOrAlias,
  getColorFromSeries,
  legendProps,
  clickInteractions,
  selected = [],
  onSelect,
  metricDisplayName,
  measurements,
  customLabelRenderer,
  responseTotals,
}: PieSeriesAdapterProps) => {
  const dataWithColors = data.map((datum, i) => ({
    ...datum,
    color: getColorFromSeries({
      dimValue: datum[dimNameOrAlias],
      metricValue: datum[metric],
      index: i,
    }),
  }));

  const variant = widget.type === PieSubType.Donut ? 'donut' : 'pie';

  const showTotals = widget.type === PieSubType.Donut ? !!(widget as DonutTypeWidget).definition.custom.showTotals : false;
  const metricTotal = responseTotals?.[metric];

  const renderCenter = variant === 'donut' && !showTotals ? () => null : undefined;
  const centerValue = showTotals && metricTotal ? String(abbreviateNumber(metricTotal)) : undefined;

  const getLegendType = (): LegendType => {
    const { style, position } = widget.definition.custom.legend;
    if (style === PieLegendStyles.Off || style === PieLegendStyles.Aside) return 'none';
    if (style === PieLegendStyles.Basic) {
      return position === PieBasicLegendPosition.Bottom ? 'linear-bottom' : 'linear-top';
    }
    return 'table-right';
  };

  const legendType = getLegendType();

  const dataLabelMode = widget.definition.custom.legend.style === PieLegendStyles.Aside ? ('polyline' as const) : undefined;

  const valueFormatter: Formatter<DatumWithColor> = (datum, { where }) => {
    if (where === 'data-label') {
      return String(formatNumberCompact(Number(datum[metric])));
    }
    if (where === 'polyline-label') {
      return legendProps?.showValue ? String(formatNumberCompact(Number(datum[metric]))) : '';
    }
    return datum[metric]?.toLocaleString?.() ?? String(datum[metric]);
  };

  const labelFormatter: LabelFormatter<DatumWithColor> = datum => {
    const dimValue = datum[dimNameOrAlias];
    const formatted = legendProps?.metricNameFormatter?.(dimValue) ?? dimValue;
    if (formatted === null || formatted === undefined) return NO_VALUE;
    if (customLabelRenderer) return customLabelRenderer(formatted);
    return String(formatted);
  };

  const hasInteractions = clickInteractions && clickInteractions.length > 0;
  const pinOnClick = (clickInteractions?.length ?? 0) > 1;

  const buildInteractionArgs = (datum: PieWidgetDataEntry, index: number): InteractionArgs => ({
    d: datum,
    widgetData: data,
    index,
    originalValue: datum.originalValue,
    headCellType: datum.headCellType,
  });

  const selectedIndices = new Set(selected.map(s => s.index));

  const selectedAccessor =
    selectedIndices.size > 0 ? (datum: DatumWithColor) => selectedIndices.has(dataWithColors.indexOf(datum)) : undefined;

  const handleSelectionToggle = (datum: DatumWithColor, clickedIndex: number) => {
    const isAlreadySelected = selectedIndices.has(clickedIndex);

    const nextSelected = isAlreadySelected
      ? selected.filter(s => s.index !== clickedIndex)
      : [
          ...selected,
          {
            value: [String(datum[dimNameOrAlias])],
            color: datum.color,
            index: clickedIndex,
            headCellType: datum.headCellType,
            originalValue: datum.originalValue,
          },
        ];

    onSelect?.(nextSelected, data);
  };

  const handleItemClick = (event: React.MouseEvent, { datum, index }: { datum: DatumWithColor; index: number }) => {
    if (hasInteractions) {
      const enabled = clickInteractions.filter(i => i.isEnabledForCell(datum));
      if (enabled.length === 1) {
        enabled[0].onClick(buildInteractionArgs(datum, index));
      }
      return;
    }

    if (onSelect) {
      handleSelectionToggle(datum, index);
    }
  };

  const measurementDescription = measurements.asObject[widget.requests[0].select.metrics[0].name]?.description;
  const needsTotal = legendType === 'table-right' || (dataLabelMode === 'polyline' && legendProps?.showShare);
  const total = needsTotal ? metricTotal : undefined;

  return (
    <Box className="draggableCancel" sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>
      <PieSeries
        data={dataWithColors}
        valueAccessor={(d: DatumWithColor) => Number(d[metric])}
        colorAccessor={(d: DatumWithColor) => d.color}
        variant={variant}
        legendType={legendType}
        dataLabelMode={dataLabelMode}
        valueFormatter={valueFormatter}
        labelFormatter={labelFormatter}
        pinOnClick={pinOnClick}
        onItemClick={handleItemClick}
        selectedAccessor={selectedAccessor}
        renderCenter={renderCenter}
        centerValue={centerValue}
        total={total}
        TooltipComponent={PieTooltipComponent}
        tooltipComponentProps={{ buildInteractionArgs, clickInteractions, data: dataWithColors, measurementDescription, metricDisplayName }}
      />
    </Box>
  );
};
